import React from "react";
import { Menu, MenuItem, Typography, Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface Sprint {
  _id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  goal?: string;
}

interface BacklogMenusProps {
  // Main context menu
  menuAnchorEl:
    | HTMLElement
    | { element: HTMLElement; position: { top: number; left: number } }
    | null;
  onMenuClose: () => void;
  actionTaskId: string | null;
  onEditTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;

  // Sprint submenu
  sprintMenuAnchorEl: HTMLElement | null;
  onSprintMenuClose: () => void;
  onSprintMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  availableSprints: Sprint[];
  onMoveToSprint: (sprintId: string) => void;
  isBacklogTask: (taskId: string) => boolean;
  isUpdating: boolean;

  // Move to menu (bulk operations)
  moveToMenuAnchorEl: HTMLElement | null;
  onMoveToMenuClose: () => void;
  availableMoveDestinations: Array<{
    id: string;
    label: string;
    type: string;
  }>;
  onBulkMove: (destinationId: string) => void;
  isUpdatingSprintStatus: boolean;
  sprints: Sprint[];
}

const BacklogMenus: React.FC<BacklogMenusProps> = ({
  menuAnchorEl,
  onMenuClose,
  actionTaskId,
  onEditTask,
  onDeleteTask,
  sprintMenuAnchorEl,
  onSprintMenuClose,
  onSprintMenuOpen,
  availableSprints,
  onMoveToSprint,
  isBacklogTask,
  isUpdating,
  moveToMenuAnchorEl,
  onMoveToMenuClose,
  availableMoveDestinations,
  onBulkMove,
  isUpdatingSprintStatus,
  sprints,
}) => {
  const { t } = useTranslation(["bugherd"]);

  // Check if menuAnchorEl has position object
  const hasPosition =
    menuAnchorEl &&
    typeof menuAnchorEl === "object" &&
    "position" in menuAnchorEl;
  const anchorElement = hasPosition
    ? menuAnchorEl.element
    : (menuAnchorEl as HTMLElement | null);
  const anchorPosition = hasPosition ? menuAnchorEl.position : undefined;

  return (
    <>
      {/* Main Context Menu */}
      <Menu
        anchorEl={anchorElement}
        anchorReference={hasPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={hasPosition ? anchorPosition : undefined}
        open={Boolean(menuAnchorEl)}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => onEditTask(actionTaskId || "")}>
          <Typography variant="body2">{t("Edit")}</Typography>
        </MenuItem>
        <MenuItem onClick={() => onDeleteTask(actionTaskId || "")}>
          <Typography variant="body2">{t("Delete")}</Typography>
        </MenuItem>

        {actionTaskId && isBacklogTask(actionTaskId) && (
          <MenuItem
            disabled={availableSprints.length === 0}
            onClick={(e) => {
              if (availableSprints.length > 0) {
                onSprintMenuOpen(e);
              }
            }}
            sx={{
              position: "relative",
              "&:hover": {
                backgroundColor:
                  availableSprints.length > 0 ? "action.hover" : "transparent",
              },
            }}
          >
            <Typography
              variant="body2"
              color={
                availableSprints.length === 0 ? "text.disabled" : "text.primary"
              }
            >
              {t("Move to Sprint")}
            </Typography>
            {availableSprints.length > 0 && (
              <Typography
                variant="body2"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                →
              </Typography>
            )}
          </MenuItem>
        )}
      </Menu>

      {/* Sprint Submenu */}
      <Menu
        anchorEl={sprintMenuAnchorEl}
        open={Boolean(sprintMenuAnchorEl)}
        onClose={onSprintMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            marginLeft: 1,
          },
        }}
      >
        {availableSprints.map((sprint: Sprint) => (
          <MenuItem
            key={sprint._id}
            onClick={() => onMoveToSprint(sprint._id)}
            disabled={isUpdating}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">{sprint.name}</Typography>
              <Chip
                label={sprint.status}
                size="small"
                color={sprint.status === "ACTIVE" ? "primary" : "default"}
                variant="outlined"
                sx={{ fontSize: "0.6rem", height: 16 }}
              />
            </Box>
          </MenuItem>
        ))}
        {availableSprints.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t("No available sprints")}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Move To Menu (Bulk Operations) */}
      <Menu
        anchorEl={moveToMenuAnchorEl}
        open={Boolean(moveToMenuAnchorEl)}
        onClose={onMoveToMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {availableMoveDestinations.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t("No available destinations")}
            </Typography>
          </MenuItem>
        ) : (
          availableMoveDestinations.map((destination) => (
            <MenuItem
              key={destination.id}
              onClick={() => onBulkMove(destination.id)}
              disabled={isUpdating || isUpdatingSprintStatus}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">{destination.label}</Typography>
                {destination.type === "sprint" && (
                  <Chip
                    label={
                      sprints.find((s) => `sprint-${s._id}` === destination.id)
                        ?.status || "DRAFT"
                    }
                    size="small"
                    color={
                      sprints.find((s) => `sprint-${s._id}` === destination.id)
                        ?.status === "ACTIVE"
                        ? "primary"
                        : "default"
                    }
                    variant="outlined"
                    sx={{ fontSize: "0.6rem", height: 16 }}
                  />
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default BacklogMenus;
